/**
 * flow-catalog (US-AI.F2-0231 · @UC9400) — FLOW_CATALOG IMMUTABLE: mọi flow AI-first seed sẵn trong CODE.
 *
 * Mỗi flow trace về một mã scenario (docs/ai-scenarios/registry.json). Admin chỉ chỉnh `configurable`
 * (enabled/priority/template/thresholds) — KHÔNG thêm/xoá flow ở runtime ⇒ hành vi tất định, review được.
 *
 * Đợt B1 phủ nhóm G1-onboarding + G2-ambiguous (engine core):
 *  - @AI-ONB-03 — user chưa có nhóm / owner chưa có quỹ / chưa biết AI làm được gì → flow onboarding + deep-link.
 *  - @AI-ONB-09 — lời chào cá nhân hoá từ ctx.displayName THẬT; thiếu tên → "Chào bạn" (KHÔNG bịa "chị Minh").
 *  - @SEC-ONB-01 — cảnh báo NẶNG một-lần trước lần AI-ghi-tiền ĐẦU TIÊN (bút toán vào sổ cái THẬT).
 *  - @AI-AMB-16 — mọi quỹ nhắc tới PHẢI có trong ctx.funds (rule-engine lọc; engine không bịa quỹ).
 */
import { NO_DEADLINE } from "./signals.js";
import type { Flow } from "./types.js";

/** deep-freeze nông (flow + configurable + steps) — chống sửa catalog ở runtime. */
function freezeFlow(f: Flow): Flow {
  Object.freeze(f.configurable);
  f.steps.forEach((s) => Object.freeze(s));
  Object.freeze(f.steps);
  Object.freeze(f.trigger);
  return Object.freeze(f);
}

const FLOWS: Flow[] = [
  // ── BLOCKED — đang bị khoá, người dùng KHÔNG tự gỡ được ─────────────────────────────────────
  //
  // 🔴 `0438` đã BỎ NÚT khi nhóm/quỹ bị khoá, nhưng không nói ra lý do ⇒ người dùng thấy thẻ mất nút
  // mà tưởng app hỏng, hoặc tệ hơn: trợ lý vẫn mời "ghi khoản chi" rồi mọi thao tác trả 409.
  // Cả ba `ctalessReason: "blocked"` — không hành động nào của người dùng sửa được.
  {
    // `BLK-03` — KHÔNG rút được vai. Đứng TRÊN mọi thẻ khác (kể cả `AI-DIS-22` 190): engine không
    // biết người này được phép làm gì, nên **mọi gợi ý khác đều là đoán**.
    id: "BLK-ROLE-UNKNOWN",
    code: "AI-DIS-22",
    kind: "proactive",
    trigger: {
      rule: "ROLE_UNKNOWN",
      roles: ["*"],
      when: [{ signal: "roleUnknownInGroup", op: "truthy" }],
    },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "BLOCKED",
      itemKind: "task",
      priority: 196,
      templateVi:
        "Mình chưa xác định được vai của bạn trong nhóm nên tạm chỉ mở phần xem.",
      templateEn: "I could not determine your role in the group, so only viewing is available.",
      detailVi: "Bạn thử mở lại ứng dụng; nếu vẫn vậy thì nhờ chủ nhóm kiểm tra lại phần thành viên.",
      detailEn: "Try reopening the app; if it persists, ask the group owner to check the member list.",
      ctalessReason: "blocked",
    },
  },

  {
    // `BLK-01` — nhóm tạm dừng: WRITE bị chặn, READ vẫn mở. Nói rõ vế thứ hai, nếu không người dùng
    // tưởng mất sạch dữ liệu.
    id: "BLK-GROUP-SUSPENDED",
    code: "AI-DIS-22",
    kind: "proactive",
    trigger: {
      rule: "GROUP_SUSPENDED",
      roles: ["*"],
      when: [{ signal: "hasSuspendedGroup", op: "truthy" }],
    },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "BLOCKED",
      itemKind: "task",
      priority: 185,
      templateVi: "Nhóm {tenNhom} đang tạm dừng — bạn vẫn xem được sổ, nhưng chưa ghi thêm được.",
      templateEn: "Group {tenNhom} is suspended — you can still view the books but cannot add entries.",
      detailVi: "Số liệu cũ vẫn còn nguyên. Chủ nhóm liên hệ bộ phận hỗ trợ để mở lại.",
      detailEn: "Existing data is intact. The group owner can contact support to reactivate it.",
      ctalessReason: "blocked",
    },
  },

  {
    // `BLK-02` — quỹ đóng băng: mọi thao tác tiền trả 409.
    id: "BLK-FUND-FROZEN",
    code: "AI-DIS-22",
    kind: "proactive",
    trigger: {
      rule: "FUND_FROZEN",
      roles: ["*"],
      when: [{ signal: "hasFrozenFund", op: "truthy" }],
    },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "BLOCKED",
      itemKind: "task",
      priority: 184,
      templateVi: "Quỹ {tenQuy} đang tạm khoá — chưa nộp hay chi được lúc này.",
      templateEn: "Fund {tenQuy} is locked — you cannot pay in or spend right now.",
      detailVi: "Sổ vẫn xem được bình thường; khoá chỉ chặn các thao tác làm thay đổi số dư.",
      detailEn: "The books stay readable; the lock only blocks actions that change the balance.",
      ctalessReason: "blocked",
    },
  },

  // ── IN_FLIGHT — việc BẠN đang làm dở ────────────────────────────────────────────────────────
  //
  // 🔴 `AiFormSession.expiresAt` **âm thầm giết** phiên đang điền. Người dùng điền xong một form TIỀN,
  // quay lại bấm Xác nhận và nhận lỗi — không ai từng báo họ rằng có thứ đang chạy ngược đồng hồ.
  // CTA là **chip gửi câu**: hành động đúng là *tiếp tục hội thoại tại chỗ*, không phải điều hướng đi.
  {
    id: "FLY-FORM-CONFIRM",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "FORM_AWAITING_CONFIRM",
      roles: ["*"],
      when: [{ signal: "hasConfirmPendingForm", op: "truthy" }],
    },
    steps: [
      { id: "msg", type: "message" },
      {
        id: "cta",
        type: "deep_link",
        chipPrompt: "Tiếp tục việc đang làm dở",
        // 🔴 `"resume"` = turn phải mang `resumingForm`. Chưa route được hôm nay (rơi câu mời
        // chung) ⇒ nằm trong `KNOWN_UNROUTABLE_CHIPS`, chủ là `US-AI-0475` (T-04a).
        chipExpect: { type: "resume" },
      },
    ],
    configurable: {
      enabled: true,
      band: "IN_FLIGHT",
      itemKind: "task",
      // Trên `collecting`: ở đây chỉ còn MỘT cú bấm là tiền vào sổ.
      priority: 195,
      templateVi: "Bạn có việc đã điền xong, chỉ còn chờ bạn xác nhận.",
      templateEn: "You have an item filled in and waiting only for your confirmation.",
      detailVi: "Chỉ còn một bước xác nhận là xong. Phiên để lâu quá sẽ hết hạn và phải làm lại từ đầu.",
      detailEn: "One confirmation away. If left too long the session expires and you start over.",
      ctaLabelVi: "Xác nhận nốt",
      ctaLabelEn: "Finish confirming",
    },
  },

  {
    id: "FLY-FORM-COLLECTING",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "FORM_COLLECTING",
      roles: ["*"],
      when: [{ signal: "hasDraftForm", op: "truthy" }],
    },
    steps: [
      { id: "msg", type: "message" },
      {
        id: "cta",
        type: "deep_link",
        chipPrompt: "Tiếp tục việc đang làm dở",
        // 🔴 `"resume"` = turn phải mang `resumingForm`. Chưa route được hôm nay (rơi câu mời
        // chung) ⇒ nằm trong `KNOWN_UNROUTABLE_CHIPS`, chủ là `US-AI-0475` (T-04a).
        chipExpect: { type: "resume" },
      },
    ],
    configurable: {
      enabled: true,
      band: "IN_FLIGHT",
      itemKind: "task",
      priority: 190,
      templateVi: "Bạn đang có việc điền dở.",
      templateEn: "You have an item half-filled.",
      detailVi: "Phiên để lâu quá sẽ hết hạn và những gì bạn đã điền sẽ mất.",
      detailEn: "If left too long the session expires and what you filled in is lost.",
      ctaLabelVi: "Làm tiếp",
      ctaLabelEn: "Continue",
    },
  },

  {
    // `FLY-05` — người BỊ HẠ VAI mà còn thẻ đề xuất treo.
    // 🔴 `SEC-CTX-02` chỉ cho vai quản trị, nên người vừa bị hạ xuống `member` **không nhận gì** và
    // tưởng đề xuất đã trôi. Nhưng được phục chức là **thẻ cũ NỔ** — bút toán ngủ đông chờ nổ.
    // Cùng sự kiện, khác vai, khác câu chữ ⇒ flow riêng thay vì nới `roles` của flow kia (nới sẽ làm
    // member thường nhận cảnh báo về thẻ họ không tạo được).
    id: "SEC-CTX-02-DEMOTED",
    code: "SEC-CTX-02",
    kind: "proactive",
    trigger: {
      rule: "STALE_PROPOSALS_DEMOTED",
      roles: ["member"],
      when: [{ signal: "stalePendingProposals", op: "gt", value: 0 }],
    },
    vars: { soViec: "stalePendingProposals" },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "IN_FLIGHT",
      itemKind: "warning",
      priority: 185,
      templateVi: "Bạn còn {soViec} đề xuất cũ chưa được xử lý.",
      templateEn: "You still have {soViec} old proposal(s) that were never resolved.",
      detailVi: "Chúng vẫn nằm trên hệ thống. Nếu quyền của bạn được khôi phục, chúng có thể được thực hiện.",
      detailEn: "They are still on the system. If your permissions are restored they may still execute.",
      ctalessReason: "waiting_on_others",
    },
  },

  // ── DEADLINE — hạn CỨNG, bỏ lỡ là không hoàn tác ────────────────────────────────────────────
  //
  // 🔴 Vì sao bậc này trên `DUTY`: không phải "quan trọng hơn tiền", mà **khác loại**. Khoản nợ còn
  // nguyên, mở app ngày mai nhắc lại vẫn kịp. Hạn chót thì không.
  // 🔴 MẶC ĐỊNH TẮT (`zeroDisables`) — thêm lời nhắc cho mọi nhóm đang chạy là quyết định của PO.
  {
    // `DDL-01` — khoản chi đang "nguội": tới hạn là **tiền tự rời quỹ**, cửa sổ huỷ đóng lại.
    id: "DDL-COOLING-EXPENSE",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "COOLING_EXPENSE_DUE",
      roles: ["owner", "admin", "treasurer"],
      when: [
        {
          signal: "coolingExpenseHoursLeft",
          op: "lte",
          value: { threshold: "hoursBeforePost", fallback: 0, zeroDisables: true },
        },
      ],
    },
    // Chưa có màn "khoản chi đang nguội" ổn định trên client ⇒ thẻ THẬT THÀ không nút.
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "DEADLINE",
      itemKind: "task",
      priority: 170,
      templateVi: "Có khoản chi sắp tự động ghi sổ khi hết thời gian chờ.",
      templateEn: "An expense is about to post automatically when its waiting period ends.",
      detailVi: "Sau thời điểm đó tiền rời quỹ và không huỷ được nữa — chỉ có thể ghi thêm một khoản điều chỉnh.",
      detailEn: "After that the money leaves the fund and cannot be cancelled — only reversed by a new entry.",
      ctalessReason: "informational",
    },
  },

  {
    // `DDL-02` — phiếu sắp hết hạn. Hết hạn ⇒ phiên LUÔN `failed`: "im lặng ≠ uỷ nhiệm".
    id: "DDL-VOTE-DEADLINE",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "VOTE_DEADLINE_NEAR",
      roles: ["*"],
      when: [
        {
          signal: "voteDeadlineHoursLeft",
          op: "lte",
          value: { threshold: "hoursBeforeDeadline", fallback: 0, zeroDisables: true },
        },
      ],
    },
    steps: [
      { id: "msg", type: "message" },
      { id: "cta", type: "deep_link", deepLinkKey: "VOTE_PROPOSAL" },
    ],
    configurable: {
      enabled: true,
      band: "DEADLINE",
      itemKind: "task",
      priority: 160,
      templateVi: "Có phiếu sắp hết hạn mà bạn chưa bỏ.",
      templateEn: "A vote is about to close and you have not cast yours.",
      detailVi: "Hết hạn thì phiên khép lại là KHÔNG THÔNG QUA — im lặng không được tính là đồng ý.",
      detailEn: "When it closes the session fails — silence is never counted as approval.",
      ctaLabelVi: "Bỏ phiếu",
      ctaLabelEn: "Cast your vote",
    },
  },

  {
    // `DDL-03a` — người BỊ NÊU TÊN trong yêu cầu chuyển quyền chủ nhóm.
    // 🔴 Họ PHẢI thấy: chỉ cần họ mở app (heartbeat) là mọi yêu cầu đang mở bị HUỶ NGAY. Không biết
    // = mất quyền nhóm vì im lặng.
    // 🔴 KHÔNG NÚT: chuyển quyền sở hữu là hành động PHÁ HUỶ — CLAUDE.md §4 cấm thẻ Xác nhận trong chat.
    id: "DDL-OWNER-TRANSFER-TARGET",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "OWNER_TRANSFER_OBJECTION_TARGET",
      roles: ["*"],
      when: [
        { signal: "ownerTransferTargetsMe", op: "truthy" },
        { signal: "ownerTransferObjectionHoursLeft", op: "lt", value: NO_DEADLINE },
      ],
    },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "DEADLINE",
      itemKind: "task",
      priority: 175,
      templateVi: "Nhóm đang có yêu cầu chuyển quyền chủ nhóm khỏi bạn.",
      templateEn: "There is an open request to transfer group ownership away from you.",
      detailVi: "Bạn còn thời gian phản hồi. Mở ứng dụng và thao tác trong phần cài đặt nhóm.",
      detailEn: "You still have time to respond. Open the app and act in the group settings.",
      ctalessReason: "blocked",
    },
  },

  {
    // `DDL-03b` — cử tri: cùng sự kiện, **câu chữ khác hẳn**. Nói với họ y như nói với người bị nêu
    // tên thì hoặc doạ nhầm, hoặc không nói rõ người kia đang mất gì.
    id: "DDL-OWNER-TRANSFER-VOTER",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "OWNER_TRANSFER_OBJECTION_VOTER",
      roles: ["*"],
      when: [
        { signal: "ownerTransferTargetsMe", op: "falsy" },
        { signal: "ownerTransferObjectionHoursLeft", op: "lt", value: NO_DEADLINE },
      ],
    },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "DEADLINE",
      itemKind: "task",
      priority: 165,
      templateVi: "Nhóm đang có yêu cầu đổi chủ nhóm, còn trong thời gian nêu ý kiến.",
      templateEn: "The group has an open request to change its owner; the objection window is still open.",
      detailVi: "Hết thời gian mà không ai nêu ý kiến thì thay đổi có hiệu lực.",
      detailEn: "If nobody objects before it ends, the change takes effect.",
      ctalessReason: "informational",
    },
  },

  // ── DUTY — nghĩa vụ tiền của chính người đọc ────────────────────────────────────────────────
  {
    // 🔴🔴 US-AI-0440-S04 (@UC9695 · @gate money) — "ĐÃ BÁO CHUYỂN KHOẢN, CHỜ ĐỐI CHIẾU".
    //
    // Đây là **trạng thái trung thực thứ ba** mà CLAUDE.md §3.7 đòi: owed / claimed / confirmed.
    // Trước flow này, người bấm "tôi đã chuyển" thấy khoản nợ **biến mất** khỏi lời chào và không có
    // gì thay thế ⇒ họ đọc sự im lặng thành "xong rồi". Máy KHÔNG BAO GIỜ được tự nói "đã nhận" —
    // nhưng cũng không được im.
    //
    // `priority: 78` — DƯỚI khoản chưa báo (80): việc chưa làm cấp bách hơn việc đã làm và đang chờ.
    // `ctalessReason: "waiting_on_others"` — người này KHÔNG còn hành động nào; dựng nút là mời họ đi
    // báo lần thứ hai (và có thể tạo bút toán trùng).
    id: "DUTY-CLAIMED",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "AWAITING_CONFIRMATION",
      // Tiền của CHÍNH người đọc ⇒ mọi vai, không lọc.
      roles: ["*"],
      when: [{ signal: "awaitingConfirmationCount", op: "gt", value: 0 }],
    },
    dueSource: "claimed",
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "DUTY",
      itemKind: "task",
      priority: 78,
      templateVi:
        "Bạn đã báo chuyển {soTien} cho {tenQuy} (đợt {tenDot}) — đang chờ người giữ quỹ xác nhận đã nhận.",
      templateEn:
        "You reported paying {soTien} to {tenQuy} ({tenDot}) — waiting for the treasurer to confirm.",
      detailVi:
        "Khoản này chưa tính vào số dư quỹ cho tới khi người giữ quỹ xác nhận đã nhận. Bạn không cần làm gì thêm.",
      detailEn:
        "This does not affect the fund balance until the treasurer confirms receipt. Nothing more for you to do.",
      ctalessReason: "waiting_on_others",
    },
  },

  {
    // 🔴 US-AI-0440-S04 (@UC9695 · @gate money) — NỢ NGƯỜI↔NGƯỜI.
    //
    // Nợ peer không đi qua quỹ nên KHÔNG có đợt thu nào nhắc hộ: không nhắc ở đây thì nó im lặng cho
    // tới lúc ai đó phải mở lời đòi — đúng thứ app sinh ra để tránh (P4.3 *"máy giữ sổ, người giữ tình"*).
    //
    // 🔴 KHÔNG nói số tiền: con số là kết quả netting hai chiều, đọc đúng ở màn đối soát có kiểm
    // quyền. Lời chào chỉ báo "có việc" + dẫn tới đó.
    // 🔴 Hiếu hỉ KHÔNG tính — nguồn tín hiệu là `hasOpenPeerDebt` (chỉ `debt_entries`).
    id: "DUTY-PEER-DEBT",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "PEER_DEBT_OPEN",
      roles: ["*"],
      when: [{ signal: "hasPeerDebt", op: "truthy" }],
    },
    steps: [
      { id: "msg", type: "message" },
      // Chip chạy trong chat ⇒ luôn dùng được, không phụ thuộc màn client đã dựng.
      {
        id: "cta",
        type: "deep_link",
        // Đúng thuật ngữ người dùng của repo (P5.3: Settlement → "Ai trả ai"). ⚠️ Nhưng router
        // CHƯA hiểu câu này (đo 19/08 ⇒ câu mời chung) — read-tool `peer_debts` là `US-AI-0494`.
        chipPrompt: "Ai trả ai",
        chipExpect: { type: "answer", query: "peer_debts" },
      },
    ],
    configurable: {
      enabled: true,
      band: "DUTY",
      itemKind: "task",
      priority: 76,
      templateVi: "Bạn còn khoản chưa thanh toán với người khác trong nhóm.",
      templateEn: "You still have an unsettled amount with someone in your group.",
      detailVi: "Đây là khoản giữa bạn và thành viên khác, không đi qua quỹ nên không có đợt thu nào nhắc.",
      detailEn: "This is between you and another member — it does not go through the fund, so no collection reminds you.",
      ctaLabelVi: "Xem ai trả ai",
      ctaLabelEn: "See who owes whom",
    },
  },

  {
    // 🔴 US-AI-0440-S04 (@UC9695) — YÊU CẦU VÀO NHÓM CHỜ TÔI DUYỆT.
    // Người xin vào không có kênh nào giục; im lặng ở đây nghĩa là họ chờ vô hạn mà không biết vì sao.
    id: "DUTY-JOIN-REQUESTS",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "PENDING_JOIN_REQUESTS",
      roles: ["owner", "admin", "treasurer"],
      when: [{ signal: "pendingJoinRequests", op: "gt", value: 0 }],
    },
    vars: { soViec: "pendingJoinRequests" },
    // 🔴 KHÔNG có nút — kết luận SAU KHI ĐO, không phải bỏ quên.
    // Router chưa có năng lực "duyệt yêu cầu vào nhóm": "Ai đang chờ vào nhóm" / "Xem yêu cầu vào
    // nhóm" đều rơi vào `unsupported`; "Yêu cầu tham gia" chỉ trúng câu trả lời CHUNG CHUNG ("Mình ở
    // đây để giúp bạn…") — dùng nó sẽ QUA MẶT guard AC4 mà vẫn dẫn người dùng tới hư không. Client
    // cũng chưa có màn tương ứng, và task này cấm thêm khoá ROUTE_CATALOG mới.
    // ⇒ Thà một thẻ THẬT THÀ không nút còn hơn một nút nói dối (DP-7 fail-visible).
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "DUTY",
      itemKind: "task",
      priority: 74,
      templateVi: "Có {soViec} người đang chờ bạn duyệt vào nhóm.",
      templateEn: "{soViec} people are waiting for you to approve their request to join.",
      detailVi: "Họ không có cách nào giục bạn; nếu không ai duyệt, họ chờ mà không biết vì sao.",
      detailEn: "They have no way to nudge you — without approval they wait without knowing why.",
      ctalessReason: "informational",
    },
  },

  {
    // 🔴🔴 US-AI-0440-S04 (@UC9695) — ĐỢT THU QUÁ HẠN MÀ CHƯA AI ĐÓNG.
    //
    // QĐ người dùng 17/08 (§6.1): thẻ này nói **SỐ LƯỢNG**, tuyệt đối KHÔNG nêu tên người chưa đóng.
    // Lời chào là kênh ĐẨY — mọi thành viên mở app đều thấy; bêu tên ở đây là biến lời chào thành
    // bảng điểm danh. Tên nằm sau một cú bấm, trên màn có kiểm quyền.
    //
    // 🔴 MẶC ĐỊNH TẮT: `minOverdueCollections` không khai ⇒ fallback **0** ⇒ điều kiện `> 0` không bao
    // giờ thoả ⇒ 0 REGRESS cho mọi nhóm đang chạy. Bật một lời nhắc mới cho tất cả là quyết định của
    // PO, không phải mặc định của agent (CLAUDE.md §3.8).
    id: "DUTY-OVERDUE-COLLECTION",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "OVERDUE_COLLECTION_NO_PAYER",
      roles: ["owner", "admin", "treasurer"],
      when: [
        {
          signal: "overdueCollectionsNoPayer",
          op: "gte",
          // `zeroDisables` — ngưỡng 0 (mặc định) ⇒ TẮT HẲN, không phải "≥ 0" (vốn luôn đúng).
          value: { threshold: "minOverdueCollections", fallback: 0, zeroDisables: true },
        },
      ],
    },
    vars: { soViec: "overdueCollectionsNoPayer" },
    steps: [
      { id: "msg", type: "message" },
      {
        id: "cta",
        type: "deep_link",
        // 🔴🔴 US-AI-0486 lượt 1 (@UC9775) — THÊM KỲ. Câu cũ *"Ai chưa đóng"* ra `clarify` hỏi
        // *"kỳ nào?"*: người dùng bấm đúng nút app mời rồi **bị hỏi ngược**. Thiếu đúng một thứ là
        // KỲ, và router đã hiểu *"tháng này"* từ trước (`query-intent.ts`; `AI_TOOL_REGISTRY` dùng
        // đúng lối này cho chip `queryUnpaid`).
        //
        // 🔴 DÙNG "tháng này", KHÔNG ghi cứng số tháng: nhãn chip là thứ người dùng ĐỌC rồi mới bấm,
        // ghi cứng *"tháng 7"* là in một con số SAI ngay trên nút (đúng bệnh `US-AI-0470` đang chữa
        // cho `AI_CAPABILITIES`). "tháng này" tự đúng ở mọi tháng, không cần ai truyền tháng xuống.
        //
        // 🔴 P5.3 — câu hỏi về KỲ, KHÔNG nêu tên người nào. Kênh NHẮC là riêng tư tuyệt đối; danh
        // sách trạng thái vẫn gác bằng `payment_status_visibility` ở tầng thực thi, không ở đây.
        chipPrompt: "Ai chưa đóng tháng này?",
        chipExpect: { type: "answer", query: "unpaid" },
      },
    ],
    configurable: {
      enabled: true,
      band: "DUTY",
      itemKind: "task",
      priority: 72,
      templateVi: "Có {soViec} đợt thu đã quá hạn mà chưa ai đóng.",
      templateEn: "{soViec} collections are overdue with nobody having paid yet.",
      // 🔴 US-AI-0486 lượt 1 (@UC9775) — NHÃN KHÔNG ĐƯỢC NÓI DỐI VỀ HÀNH VI. `step-runner` dựng nhãn
      // bằng `ctaLabelVi ?? chipPrompt`; nút này là **chip chạy trong chat**, nên nhãn *"Mở bảng đối
      // soát"* hứa một MÀN HÌNH mà bấm vào lại nhận một câu trả lời trong khung chat. Đổi ngõ cụt lấy
      // ngõ cụt thì chưa đạt yêu cầu *"chip định tuyến được"*. (Repo đã đặt tên cho luật này ở
      // `capability-chips.ts`: *"nhãn không nói dối về hành vi"*.)
      ctaLabelVi: "Xem ai chưa đóng",
      ctaLabelEn: "See who hasn't paid",
    },
  },

  // ── SETUP (lối vào) — BA cách vào một nhóm ĐÃ CÓ, hôm nay vô hình ───────────────────────────
  //
  // 🔴🔴 US-AI-0441-S01 (@UC9696). Cả ba cùng khớp `hasAnyGroup = false` với `NO_GROUP`, nên người
  // đang có lời mời / đã có ghế / đang chờ duyệt đều nhận *"Bạn chưa có nhóm nào — tạo nhóm quỹ đầu
  // tiên nhé?"* ⇒ **đẻ nhóm rác song song với lớp thật**, và ghế treo trong lớp không ai nhận.
  //
  // Ba flow này ưu tiên TRÊN `NO_GROUP` (100) nhưng CÙNG bậc `SETUP` ⇒ `NO_GROUP` vẫn hiện: người
  // vừa có lời mời vừa muốn lập nhóm riêng là chuyện có thật. Muốn nó biến mất hẳn là một LUẬT LOẠI
  // TRỪ — cần PO quyết, không phải mặc định của agent (ghi ở `index.md` §6).
  {
    // `SET-01` biến thể NÊU TÊN — chỉ khi khớp `matchedUserId = me` và có ĐÚNG một lời mời.
    id: "SET-INVITATION-NAMED",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "INVITATION_PENDING_NAMED",
      roles: ["*"],
      when: [{ signal: "namedInvitationGroup", op: "truthy" }],
    },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "SETUP",
      itemKind: "task",
      priority: 111,
      templateVi: "Bạn đang được mời vào nhóm {tenNhom}.",
      templateEn: "You have been invited to join {tenNhom}.",
      detailVi:
        "Mở lời mời để xem nhóm này thu chi những gì rồi quyết định tham gia hay không.",
      detailEn: "Open the invitation to see what this group collects and spends, then decide.",
      nextVi: "Vào nhóm xong, mình sẽ nhắc bạn các khoản cần đóng ngay tại đây.",
      nextEn: "Once you join, I will remind you about your dues right here.",
      // 🔴 DP-7 — CHƯA có màn "lời mời của tôi" trên client (`endUserClientRoutes` không có khoá nào).
      // Thà nói rõ "chỉ để biết" còn hơn dựng nút dẫn vào hư không.
      ctalessReason: "informational",
    },
  },
  {
    // `SET-01` biến thể CHỈ ĐẾM — khớp qua `matchKeyValue` (email/phone CHƯA xác thực) hoặc nhiều
    // hơn một lời mời. 🔴 Đây là chốt PII lớn nhất của lane: xem docblock `pendingInvitationGroupName`.
    id: "SET-INVITATION-PENDING",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "INVITATION_PENDING",
      roles: ["*"],
      when: [
        { signal: "pendingInvitations", op: "gt", value: 0 },
        // Loại trừ với biến thể nêu tên bằng CHÍNH dữ liệu — không cần luật triệt tiêu tay.
        { signal: "namedInvitationGroup", op: "falsy" },
      ],
    },
    vars: { soViec: "pendingInvitations" },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "SETUP",
      itemKind: "task",
      priority: 110,
      // 🔴 KHÔNG `{tenNhom}` ở đây, và đó là toàn bộ điểm của biến thể này.
      templateVi: "Bạn đang có {soViec} lời mời vào nhóm đang chờ trả lời.",
      templateEn: "You have {soViec} pending group invitation(s) waiting for your reply.",
      detailVi: "Mở phần lời mời để xem chi tiết rồi quyết định tham gia hay không.",
      detailEn: "Open your invitations to see the details and decide whether to join.",
      nextVi: "Vào nhóm xong, mình sẽ nhắc bạn các khoản cần đóng ngay tại đây.",
      nextEn: "Once you join, I will remind you about your dues right here.",
      ctalessReason: "informational",
    },
  },
  {
    // `SET-02` — có GHẾ gắn với mình mà chưa/không còn `active`: ghost import từ CSV, phụ huynh thứ
    // hai qua `MembershipAccount`, ghế vừa bị gỡ. 0 truy vấn mới: `affiliatedSeats` do 0437-S04 mang
    // vào ctx, ở đây chỉ đọc lại.
    id: "SET-UNCLAIMED-SEAT",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "UNCLAIMED_SEAT",
      roles: ["*"],
      when: [
        { signal: "groupCount", op: "eq", value: 0 },
        { signal: "affiliatedSeats", op: "gt", value: 0 },
      ],
    },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "SETUP",
      itemKind: "task",
      priority: 105,
      // Không nêu tên nhóm: ghế có thể do người khác import, cùng lập luận PII với lời mời khớp-khoá.
      templateVi: "Có một chỗ trong nhóm đang gắn với bạn nhưng chưa được nhận.",
      templateEn: "A seat in a group is linked to you but has not been claimed yet.",
      detailVi:
        "Thường là do người quản lý nhóm nhập danh sách trước. Nhờ họ gửi lời mời để bạn nhận chỗ này.",
      detailEn:
        "This usually happens when a manager imports the roster first. Ask them to send you an invitation.",
      // 🔴 DP-7 — CHƯA có màn "nhận ghế" trên client. Không thêm khoá route mới (spec §6:
      // `MAX_MISSING_IN_REBUILD=9`, so BẰNG).
      ctalessReason: "informational",
    },
  },
  {
    // `SET-03` — tôi đã xin vào nhóm, đang chờ duyệt. Priority DƯỚI `NO_GROUP` (100) là CỐ Ý: người
    // đang chờ vẫn nên được mời lập nhóm riêng nếu họ chưa ở đâu; nhưng câu "đang chờ duyệt" phải
    // xuất hiện để họ không tưởng app quên mình.
    id: "SET-JOIN-REQUEST-PENDING",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "MY_JOIN_REQUEST_PENDING",
      roles: ["*"],
      when: [{ signal: "myPendingJoinRequests", op: "gt", value: 0 }],
    },
    vars: { soViec: "myPendingJoinRequests" },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "SETUP",
      itemKind: "task",
      priority: 95,
      templateVi: "Yêu cầu vào nhóm của bạn đang chờ người quản lý duyệt.",
      templateEn: "Your request to join a group is waiting for a manager to approve it.",
      detailVi: "Khi được duyệt, mình sẽ nhắc bạn các khoản cần đóng ngay tại đây.",
      detailEn: "Once approved, I will remind you about your dues right here.",
      // 🔴 KHÔNG NÚT, và lý do phải nói ra: ép nút vào ⇒ họ gửi lần hai ⇒ partial unique
      // `one_pending_join_request_per_user` (`schema.prisma:2052-2054`) trả lỗi.
      ctalessReason: "waiting_on_others",
    },
  },

  // ── G1 onboarding ───────────────────────────────────────────────────────────────────────────
  {
    // @AI-ONB-03 — user chưa thuộc nhóm nào: AI chủ động mời tạo nhóm quỹ đầu tiên (Home/chat có lối vào thật).
    id: "AI-ONB-03",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "NO_GROUP",
      roles: ["*"],
      when: [
        { signal: "hasAnyGroup", op: "falsy" },
        { signal: "groupCount", op: "eq", value: 0 },
        // 🔴 US-AI-0437-S04 (@UC9682) — có GHẾ liên quan tới nhóm thật (phụ huynh thứ hai, ghế
        // `unclaimed`, ghế vừa bị gỡ mà còn nợ) ⇒ KHÔNG nói "bạn chưa có nhóm nào". Ở develop vế này
        // là một `&&` trong hàm rule; sang nhánh này nó là DỮ LIỆU — cùng ngữ nghĩa, khai ở catalog.
        { signal: "affiliatedSeats", op: "eq", value: 0 },
      ],
    },
    steps: [
      { id: "msg", type: "message" },
      // 🔴 US-AI-0439-S02 (@UC9689): kèm `chipPrompt` — chip chạy TRONG chat nên luôn dùng được, kể cả
      // khi màn đích chưa dựng trên client. Câu phải đi qua ĐÚNG router hiện có (test chạy router thật).
      {
        id: "cta",
        type: "deep_link",
        deepLinkKey: "GROUP_CREATE",
        chipPrompt: "Tạo nhóm mới",
        chipExpect: { type: "form", code: "ONB-CREATE-GROUP" },
      },
    ],
    configurable: {
      enabled: true,
      band: "SETUP",
      // 🔴 US-AI-0439-S02 (@UC9689): câu chữ của thẻ nằm ở CATALOG (admin sửa được, không cần deploy).
      detailVi: "Nhóm là nơi bạn và mọi người cùng theo dõi tiền vào tiền ra của một quỹ chung.",
      detailEn: "A group is where you and everyone else follow the money going in and out of a shared fund.",
      nextVi: "Xong bước này, tiếp theo là tạo quỹ cho nhóm.",
      nextEn: "After this, the next step is creating a fund for the group.",
      ctaLabelVi: "Tạo nhóm",
      ctaLabelEn: "Create a group",
      itemKind: "task",
      priority: 100,
      templateVi: "Bạn chưa có nhóm nào — tạo nhóm quỹ đầu tiên nhé?",
      templateEn: "You have no group yet — create your first fund group?",
      // 🔴 US-AI-0443-S02 (@UC9703) — LẦN ĐẦU TUYỆT ĐỐI: người dùng chưa biết chat này làm được gì.
      // Câu thường (ngắn) đúng cho người quay lại; với người mới nó bỏ lỡ đúng khoảnh khắc quyết định
      // họ có ở lại hay không (QĐ PO 17/08 lượt 3).
      templateFirstRunVi:
        "Chỗ này ghi sổ giúp bạn: ai đã đóng, tiền đi đâu, ai còn nợ — hỏi bằng lời là được. Bắt đầu bằng việc tạo nhóm quỹ đầu tiên nhé?",
      templateFirstRunEn:
        "This is where the books live: who paid, where money went, who still owes — just ask in plain words. Shall we start by creating your first fund group?",
    },
  },
  {
    // @AI-ONB-03 — owner đã có nhóm nhưng CHƯA có quỹ: mời tạo quỹ (không để nhóm rỗng chết).
    id: "AI-ONB-03-FUND",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "OWNER_NO_FUND",
      // 🔴 US-AI-0439-S04 (@UC9691) — `treasurer` KHÔNG được bỏ sót. Backend cho thủ quỹ quyền y hệt
      // owner/admin ở xác nhận thanh toán, record-paid, quyết định chi, tạo đợt thu
      // (`payment-confirmations.controller.ts:50`, `record-paid.controller.ts:48`,
      // `expense-decision.controller.ts:62`, `form-shared.ts:97`). Catalog lời chào là chỗ DUY NHẤT
      // còn khai ["owner","admin"] ⇒ thủ quỹ đăng nhập, có việc được phép làm, mà trợ lý im lặng.
      // Lưới PK-3 bắt được ca này khi duyệt tích Descartes theo vai.
      roles: ["owner", "admin", "treasurer"],
      when: [
        { signal: "groupCount", op: "gt", value: 0 },
        { signal: "fundCount", op: "eq", value: 0 },
      ],
    },
    steps: [
      { id: "msg", type: "message" },
      {
        id: "cta",
        type: "deep_link",
        deepLinkKey: "FUND_CREATE",
        chipPrompt: "Tạo quỹ cho nhóm",
        chipExpect: { type: "form", code: "ONB-CREATE-FUND" },
      },
    ],
    configurable: {
      enabled: true,
      band: "SETUP",
      detailVi: "Quỹ là nơi ghi các khoản thu và chi; mỗi nhóm có thể có nhiều quỹ cho nhiều mục đích.",
      detailEn: "A fund is where income and expenses are recorded; a group can have several funds.",
      nextVi: "Xong bước này, tiếp theo là thêm thành viên vào nhóm.",
      nextEn: "After this, the next step is adding members to the group.",
      ctaLabelVi: "Tạo quỹ",
      ctaLabelEn: "Create a fund",
      itemKind: "task",
      priority: 90,
      templateVi: "Nhóm chưa có quỹ nào — tạo quỹ để bắt đầu thu chi minh bạch?",
      templateEn: "This group has no fund yet — create one to start tracking?",
    },
  },
  {
    // 🔴🔴 `SET-06` — NGÕ CỤT BẰNG CÂU CHỮ của THÀNH VIÊN THƯỜNG, đo được chứ không suy đoán.
    //
    // Member trong nhóm CHƯA có quỹ rơi qua **mọi** rule: `AI-ONB-03-FUND` chặn ở `roles` (đúng RBAC),
    // `MEMBER_HAS_DUE` false (không quỹ ⇒ không due hợp lệ), chip đòi `funds.length > 0`. Chỉ còn
    // `GREETING` ⇒ `isEmptyState` ⇒ *"Mình giúp gì cho quỹ hôm nay?"* — **hỏi về một cái quỹ không
    // tồn tại**, đúng câu người mới vào nhóm gặp ở lần mở trợ lý đầu tiên.
    //
    // 🔴 KHÔNG CTA `FUND_CREATE`: member bấm sẽ nhận 403 ở bước chốt (DP-7 cấm ngõ cụt) — cùng lập
    // luận khiến `AI-DIS-22` chọn `FUND_LIST` thay vì màn ghi. Câu chữ phải nói được HAI điều: nhóm
    // chưa mở quỹ, và KHI NÀO thì trợ lý có ích cho họ.
    id: "SET-MEMBER-NO-FUND",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "MEMBER_NO_FUND",
      roles: ["member"],
      when: [
        { signal: "groupCount", op: "gt", value: 0 },
        { signal: "fundCount", op: "eq", value: 0 },
      ],
    },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "SETUP",
      itemKind: "task",
      // Dưới `AI-ONB-03-FUND` (90) — hai flow loại trừ nhau theo VAI nên thứ tự chỉ có nghĩa khi đọc
      // catalog, nhưng vẫn khai để bậc SETUP có một thang rõ ràng từ trên xuống.
      priority: 88,
      templateVi: "Nhóm của bạn chưa mở quỹ nào.",
      templateEn: "Your group has not opened a fund yet.",
      detailVi:
        "Khi chủ nhóm mở quỹ, mình sẽ nhắc bạn các khoản cần đóng ngay tại đây.",
      detailEn: "Once a manager opens a fund, I will remind you about your dues right here.",
      // Người dùng KHÔNG tự làm được việc này — nói thẳng thay vì để thẻ trống không lý do.
      ctalessReason: "waiting_on_others",
    },
  },
  {
    // `SET-07` — nhóm một người là nhóm chết. CTA khả thi nhất toàn lane vì KHÔNG cần route mới:
    // `ONB_ADD_MEMBERS` có `enabledByDefault: true` ⇒ dùng chip gửi câu, chạy trong chat.
    id: "SET-SOLO-GROUP",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "SOLO_GROUP",
      roles: ["owner", "admin", "treasurer"],
      when: [{ signal: "soloManagedGroups", op: "gt", value: 0 }],
    },
    steps: [
      { id: "msg", type: "message" },
      {
        id: "cta",
        type: "deep_link",
        chipPrompt: "Thêm thành viên vào nhóm",
        chipExpect: { type: "form", code: "ONB-ADD-MEMBERS" },
      },
    ],
    configurable: {
      enabled: true,
      band: "SETUP",
      itemKind: "task",
      priority: 85,
      templateVi: "Nhóm mới chỉ có mình bạn — thêm mọi người vào để cùng theo dõi quỹ nhé?",
      templateEn: "You are the only one in the group — add everyone so you can track the fund together?",
      detailVi: "Có đủ người thì mới chia được khoản đóng và mọi người mới thấy tiền vào tiền ra.",
      detailEn: "With everyone in, you can split dues and they can all see the money moving.",
      nextVi: "Thêm người xong, tiếp theo là mở đợt thu đầu tiên.",
      nextEn: "After that, the next step is opening your first collection.",
      ctaLabelVi: "Thêm thành viên",
      ctaLabelEn: "Add members",
    },
  },
  {
    // `SET-09` — quỹ chưa có đợt thu nào là quỹ chưa dùng được.
    //
    // 🔴 CHỈ `chipPrompt`, CỐ Ý KHÔNG khai `deepLinkKey`. Bản đầu của tôi khai cả hai (bắt chước
    // `AI-ONB-03-FUND`) và `ai-deep-link-seed.int-spec.ts` ĐỎ: guard đòi **mọi** `deepLinkKey` trong
    // catalog phải được engine THẬT sinh ra với ít nhất một kịch bản demo, mà `COLLECTION_CREATE`
    // cần `singleFundId` + tín hiệu "quỹ chưa có đợt thu" — không kịch bản seed nào có.
    // Khai một đích không dựng nổi chính là bệnh "9/11 route chết" mà guard đó sinh ra để chặn.
    // Mất mát bằng 0: `enrichUnit` LUÔN ưu tiên chip khi có cả hai, nên deep-link ở đây vốn chỉ là
    // trang trí — nó không bao giờ trở thành CTA.
    id: "SET-FUND-NO-COLLECTION",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "FUND_NO_COLLECTION",
      roles: ["owner", "admin", "treasurer"],
      when: [{ signal: "managedFundsWithoutCollection", op: "gt", value: 0 }],
    },
    steps: [
      { id: "msg", type: "message" },
      {
        id: "cta",
        type: "deep_link",
        chipPrompt: "Mở đợt thu mới",
        chipExpect: { type: "form", code: "COL-EQUAL" },
      },
    ],
    configurable: {
      enabled: true,
      band: "SETUP",
      itemKind: "task",
      priority: 75,
      templateVi: "Quỹ đã có nhưng chưa mở đợt thu nào — mở đợt đầu tiên nhé?",
      templateEn: "The fund exists but has no collection yet — open the first one?",
      detailVi: "Đợt thu là cách nhóm ghi ai cần đóng bao nhiêu, và ai đã đóng rồi.",
      detailEn: "A collection records who owes how much, and who has already paid.",
      ctaLabelVi: "Mở đợt thu",
      ctaLabelEn: "Open a collection",
    },
  },
  {
    // @AI-ONB-09 — lời chào lấy tên THẬT từ ctx.displayName; thiếu tên → "Chào bạn" (KHÔNG suy đoán giới).
    id: "AI-ONB-09",
    code: "AI-ONB-09",
    kind: "proactive",
    trigger: { rule: "GREETING", roles: ["*"] },
    steps: [{ id: "greet", type: "message" }],
    configurable: {
      enabled: true,
      band: "IDLE",
      itemKind: "reactive",
      priority: 10, // chào luôn xếp SAU việc cần làm (nợ/xác nhận) — không chiếm chỗ
      // 🔴 US-AI-0309 (PO 23/07) — template CHỈ còn LỜI CHÀO, bỏ câu hỏi mở "Mình giúp gì cho quỹ
      // hôm nay?". Lý do: khi có việc cần làm, câu hỏi mở đứng giữa lời chào và việc thật làm người
      // đọc phải bước qua một câu vô nghĩa mới tới thứ họ cần. Câu hỏi mở CHỈ hợp lý khi KHÔNG có
      // việc nào — lúc đó `composeGreetingTurn` tự thêm (xem `ComposeGreetingOptions.idlePromptVi`).
      templateVi: "Chào {tenNguoiDung} 👋",
      templateEn: "Hi {tenNguoiDung} 👋",
    },
  },
  {
    // @SEC-ONB-01 — cảnh báo NẶNG một-lần TRƯỚC lần AI-ghi-tiền đầu tiên: Xác nhận KHÔNG phải "xem trước".
    //
    // 🔴🔴 US-AI-0309 — TẮT MẶC ĐỊNH THEO QUYẾT ĐỊNH PO 23/07/2026. ĐỌC HẾT TRƯỚC KHI ĐỔI LẠI.
    //
    // PO yêu cầu lời chào chỉ còn việc cần làm; khi được hỏi cảnh báo này đi đâu (3 lựa chọn: chuyển
    // sang thẻ Xác nhận lần ghi đầu · giữ dạng chú thích nhỏ · bỏ hẳn) PO chọn **BỎ HẲN**, sau khi
    // được nêu rõ hệ quả bên dưới.
    //
    // ⚠️ HỆ QUẢ ĐANG CÓ HIỆU LỰC: đây là nơi DUY NHẤT cảnh báo "sổ cái thật, append-only" tới được
    // người dùng. `buildConfirmTurn` (`apps/api/src/modules/ai/form/form-turn-builder.ts`) hardcode
    // `heavyConfirm: false`, nên thẻ Xác nhận KHÔNG hiện cảnh báo nào. Tắt flow này ⇒ trợ lý có thể
    // ghi bút toán đầu tiên mà người dùng CHƯA từng đọc dòng nào về việc sổ không xoá được.
    //
    // 🔴 CƠ CHẾ GIỮ NGUYÊN, CHỈ ĐỔI MẶC ĐỊNH: admin bật lại được qua config flow (system/group) mà
    // không cần deploy; `requiresHeavyConfirm` vẫn chạy đúng khi bật. Nếu sau này nối
    // `heavyConfirm=true` theo `isFirstAiWrite` ở thẻ Xác nhận thì đó mới là chỗ đúng của cảnh báo —
    // xem `US-AI-0309/index.md §6`.
    id: "SEC-ONB-01",
    code: "SEC-ONB-01",
    kind: "proactive",
    trigger: {
      rule: "FIRST_AI_WRITE",
      // 🔴 US-AI-0439-S04 (@UC9691) — `treasurer` KHÔNG được bỏ sót. Backend cho thủ quỹ quyền y hệt
      // owner/admin ở xác nhận thanh toán, record-paid, quyết định chi, tạo đợt thu
      // (`payment-confirmations.controller.ts:50`, `record-paid.controller.ts:48`,
      // `expense-decision.controller.ts:62`, `form-shared.ts:97`). Catalog lời chào là chỗ DUY NHẤT
      // còn khai ["owner","admin"] ⇒ thủ quỹ đăng nhập, có việc được phép làm, mà trợ lý im lặng.
      // Lưới PK-3 bắt được ca này khi duyệt tích Descartes theo vai.
      roles: ["owner", "admin", "treasurer"],
      when: [
        { signal: "isFirstAiWrite", op: "truthy" },
      ],
    },
    steps: [{ id: "warn", type: "message" }],
    configurable: {
      enabled: false,
      band: "IDLE",
      itemKind: "warning",
      priority: 200, // cao nhất: phải đọc TRƯỚC mọi việc khác (khi admin bật lại)
      templateVi:
        "Lưu ý: khoản do trợ lý ghi sẽ vào SỔ QUỸ THẬT — sổ chỉ ghi thêm, KHÔNG xoá; ghi sai thì phải ghi thêm một dòng điều chỉnh. Bạn đã hiểu chứ?",
      templateEn:
        "Note: assistant entries post to the REAL ledger — append-only, no delete; a mistake must be reversed by a counter-entry. Understood?",
      requiresHeavyConfirm: true,
    },
  },

  // ── Nudge theo trạng thái THẬT (rule đọc ctx, không LLM) ─────────────────────────────────────
  {
    // @AI-ONB-03 — member đang nợ: nhắc đóng + deep-link màn đóng tiền. Quỹ lấy từ ctx.funds (@AI-AMB-16).
    id: "AI-ONB-03-DUE",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "MEMBER_HAS_DUE",
      roles: ["owner", "admin", "treasurer", "member"],
      when: [
        { signal: "positiveDueCount", op: "gt", value: 0 },
      ],
    },
    steps: [
      { id: "msg", type: "message" },
      // 🔴 US-AI-0439-S04 (@UC9691) — CTA ưu tiên CHIP vì đích `/pay/:collectionId` **không tồn tại**
      // trên client end-user (`apps/mobile` chỉ có 9 route). Deep-link giữ nguyên cho client nào có
      // màn; nút hiển thị là chip, nên người dùng bấm là hội thoại chạy tiếp NGAY trong chat thay vì
      // nhận "đang xây dựng".
      //
      // 🔴🔴 US-AI-0482-S02 (@UC9764) — CÂU CHIP MANG {tenQuy} + {tenDot}. Nợ hai quỹ thì hai nút
      // gửi đi HAI CÂU KHÁC NHAU, nếu không thì lượt sau trợ lý vẫn không biết đang nói về khoản
      // nào — và người dùng nhìn hai nút chữ giống hệt trong lúc sắp chuyển tiền.
      //
      // 🔴 VÌ SAO GIỮ NGUYÊN CỤM ĐỘNG TỪ "Báo đã đóng tiền" dù đo được là nó SAI: đo 20/08 cho thấy
      // mọi biến thể của câu này đều ra `clarify` — kể cả bản đã thêm tên quỹ. Sửa CÁCH NÓI cho
      // router hiểu là việc của `US-AI-0486` (T-04c), và dựng đích thật `PAY-REPORT` là việc của
      // `US-AI-0496` (T-22a); cả hai đều khai đúng chuỗi này trong thẻ của họ. Đổi cụm động từ ở
      // đây là **giẫm phạm vi hai thẻ khác** và làm hỏng AC của họ, để đổi lấy một ngõ cụt khác
      // (`"Tôi đã đóng ..."` đo ra `unsupported`, không tốt hơn `clarify`). Task này chỉ làm đúng
      // phần của nó: **phân biệt được nút nào của quỹ nào**.
      {
        id: "cta",
        type: "deep_link",
        deepLinkKey: "PAY_COLLECTION",
        chipPrompt: "Báo đã đóng tiền {tenQuy} đợt {tenDot}",
        // 🔴 SỬA COMMENT MỤC (đo 20/08): bản cũ ở trên viết "router ⇒ clarify, hỏi lại đợt nào" —
        // SAI. Router hiểu chữ "Báo" là TÊN NGƯỜI và hỏi *"Mình chưa rõ số tiền của Báo"*, tức hỏi
        // SỐ TIỀN của một người không tồn tại, không hỏi đợt. Đích đúng là form tự-báo-đã-đóng
        // (`PAY-REPORT`, `US-AI-0496`); tách tên người là `US-AI-0464`. Chừng nào cả hai chưa lên,
        // chip này là **nợ có tên có chủ** trong `KNOWN_UNROUTABLE_CHIPS` (`chip-routing.ts`).
        chipExpect: { type: "form", code: "PAY-REPORT" },
      },
    ],
    configurable: {
      enabled: true,
      band: "DUTY",
      detailVi: "Đóng đúng hạn giúp quỹ có tiền chi đúng lúc, và bạn không bị nhắc lại.",
      detailEn: "Paying on time keeps the fund able to spend when needed, and stops the reminders.",
      // 🔴🔴 US-AI-0482-S02 (@UC9763) — NHÃN NÚT MANG TÊN QUỸ. Trước bản vá: nợ 2 quỹ ⇒ 2 thẻ, câu
      // phân biệt được nhưng **hai nút cùng chữ "Đóng ngay"**. Người vừa ở lớp vừa ở CLB không có
      // cách nào biết nút nào đi vào quỹ nào — trong khi thứ họ sắp làm là chuyển tiền.
      //
      // 🔴 `{tenQuy}` ở nhãn EN cố ý giữ TÊN TIẾNG VIỆT ("Pay Quỹ lớp"): tên quỹ là dữ liệu người
      // dùng đặt, dịch nó mới là bịa — và `templateEn` ngay dưới đã trộn y hệt từ trước.
      // 🔴 KHÔNG cắt độ dài ở server: server gửi tên THẬT, cắt là việc của tầng vẽ. Cắt ở đây thì
      // admin preview và app hiện hai chuỗi khác nhau.
      ctaLabelVi: "Đóng {tenQuy}",
      ctaLabelEn: "Pay {tenQuy}",
      itemKind: "task",
      priority: 80,
      templateVi: "Bạn còn khoản {soTien} chưa đóng ở {tenQuy} (đợt {tenDot}). Đóng ngay nhé?",
      templateEn: "You still owe {soTien} in {tenQuy} ({tenDot}). Pay now?",
    },
  },
  {
    // @AI-ONB-03 — admin có chuyển khoản chờ xác nhận: nhắc xác nhận (RBAC: chỉ owner/admin).
    id: "AI-ONB-03-CONFIRM",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "ADMIN_PENDING_CONFIRMATIONS",
      // 🔴 US-AI-0439-S04 (@UC9691) — `treasurer` KHÔNG được bỏ sót. Backend cho thủ quỹ quyền y hệt
      // owner/admin ở xác nhận thanh toán, record-paid, quyết định chi, tạo đợt thu
      // (`payment-confirmations.controller.ts:50`, `record-paid.controller.ts:48`,
      // `expense-decision.controller.ts:62`, `form-shared.ts:97`). Catalog lời chào là chỗ DUY NHẤT
      // còn khai ["owner","admin"] ⇒ thủ quỹ đăng nhập, có việc được phép làm, mà trợ lý im lặng.
      // Lưới PK-3 bắt được ca này khi duyệt tích Descartes theo vai.
      roles: ["owner", "admin", "treasurer"],
      when: [
        {
          signal: "pendingConfirmations",
          op: "gte",
          value: { threshold: "minPending", fallback: 1 },
        },
      ],
    },
    vars: { soViec: "pendingConfirmations" },
    steps: [
      { id: "msg", type: "message" },
      { id: "cta", type: "deep_link", deepLinkKey: "COLLECTION_BOARD" },
    ],
    configurable: {
      enabled: true,
      band: "DUTY",
      itemKind: "task",
      priority: 70,
      templateVi: "Có {soViec} khoản chuyển khoản đang chờ bạn xác nhận.",
      templateEn: "{soViec} transfer(s) await your confirmation.",
      thresholds: { minPending: 1 },
    },
  },
  {
    // @AI-ONB-03 — đang có biểu quyết mở: nhắc bỏ phiếu.
    id: "AI-ONB-03-VOTE",
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "OPEN_VOTES",
      roles: ["owner", "admin", "treasurer", "member"],
      when: [
        { signal: "openVotes", op: "gte", value: { threshold: "minOpenVotes", fallback: 1 } },
      ],
    },
    vars: { soViec: "openVotes" },
    steps: [
      { id: "msg", type: "message" },
      { id: "cta", type: "deep_link", deepLinkKey: "VOTE_PROPOSAL" },
    ],
    configurable: {
      enabled: true,
      band: "DUTY",
      itemKind: "task",
      priority: 60,
      templateVi: "Có {soViec} khoản chi đang chờ bạn biểu quyết.",
      templateEn: "{soViec} expense(s) await your vote.",
      thresholds: { minOpenVotes: 1 },
    },
  },

  // 🔴🔴 US-AI-0479 (@UC9755 · QĐ PO D-3) — HAI FLOW "chip HÀNH ĐỘNG" ĐÃ GỠ KHỎI ĐÂY.
  //
  // `AI-ONB-02-EXPENSE` + `AI-ONB-02-REMIND` được viết cho một mô hình KHÔNG TỒN TẠI trên client:
  // *"người dùng bấm một chip HÀNH ĐỘNG, server biết chip nào vừa bấm"*. Thực tế: chip trong Goppy
  // **là một CÂU** — bấm chip là gửi câu đó qua `routeTurn`. Đo 20/08 bằng grep: `ctxLoader.load()`
  // có đúng **2 call-site sản phẩm** (`ai-context.service.ts`, `preview-runner.service.ts`), **không
  // bên nào truyền `tappedChip`** ⇒ hai flow này **chưa từng khớp một lần nào trong đời**.
  //
  // Hại thật của chúng không phải "tính năng hỏng" mà là **hai dòng dối trong bảng năng lực**: người
  // rà soát đọc bảng, tin có hai lối tắt đang chạy, rồi mất công đo lại một thứ đã chết.
  //
  // 🔴 ĐỪNG THÊM LẠI dưới dạng khác. Nếu sau này client thật sự gửi được "chip nào vừa bấm", thì cửa
  // đúng vẫn là **câu** (`chipPrompt` + `chipExpect`, guard `chip-routing.ts` của `US-AI-0471`), chứ
  // không phải một tín hiệu ctx thứ hai. Lưới `signals.spec.ts` AC-signal-2 sẽ ĐỎ ngay nếu ai khai
  // lại một tín hiệu không flow nào đọc.

  // ── G9 discover: nói THẲNG khi thiếu quyền ──────────────────────────────────────────────────
  {
    // 🔴 @AI-DIS-22 — member gõ ý định ghi tiền (hoặc bấm chip ghi) TRƯỚC ĐÂY rơi vào clarify chung
    // chung ⇒ user tưởng mình gõ chưa rõ, diễn đạt lại 3-4 lần, mỗi lần chờ 30s, cuối cùng bỏ app.
    // Nay: nói THẲNG thiếu quyền + lối ra THẬT (báo chủ quỹ / xem số dư).
    id: "AI-DIS-22",
    code: "AI-DIS-22",
    kind: "reactive",
    trigger: { rule: "MEMBER_WRITE_DENIED", roles: ["member"] },
    steps: [
      { id: "msg", type: "message" },
      { id: "cta", type: "deep_link", deepLinkKey: "FUND_LIST" },
    ],
    configurable: {
      enabled: true,
      band: "IDLE",
      itemKind: "reactive",
      priority: 190, // gần cao nhất: phải trả lời ĐÚNG lý do trước mọi gợi ý khác
      templateVi:
        "Bạn đang là thành viên nên không thể tự ghi tiền — việc ghi sổ do chủ quỹ/quản trị làm. Bạn có thể báo cho chủ quỹ, hoặc xem số dư và khoản của mình.",
      templateEn:
        "You are a member, so you cannot post entries — only owners/admins can. Tell your fund owner, or view your balance and dues.",
    },
  },

  // ── G6 context: đề xuất cũ treo (thủ quỹ bị cách chức) ──────────────────────────────────────
  {
    // 🔴 @SEC-CTX-02 — thủ quỹ bị cách chức, 5 thẻ đề xuất cũ vẫn `resolved=false` NẰM CHỜ. RBAC re-verify
    // chặn lúc confirm (403 — đúng), NHƯNG được phục chức là nổ. Flow này BÁO owner/admin số thẻ TREO
    // quá ngưỡng `staleAfterMinutes` (admin chỉnh được qua ai_flow_config — US-AI.F3-0232) để HUỶ.
    // Ngưỡng = tham số config, KHÔNG hardcode trong luật.
    id: "SEC-CTX-02",
    code: "SEC-CTX-02",
    kind: "proactive",
    trigger: {
      rule: "STALE_PENDING_PROPOSALS",
      // 🔴 US-AI-0439-S04 (@UC9691) — `treasurer` KHÔNG được bỏ sót. Backend cho thủ quỹ quyền y hệt
      // owner/admin ở xác nhận thanh toán, record-paid, quyết định chi, tạo đợt thu
      // (`payment-confirmations.controller.ts:50`, `record-paid.controller.ts:48`,
      // `expense-decision.controller.ts:62`, `form-shared.ts:97`). Catalog lời chào là chỗ DUY NHẤT
      // còn khai ["owner","admin"] ⇒ thủ quỹ đăng nhập, có việc được phép làm, mà trợ lý im lặng.
      // Lưới PK-3 bắt được ca này khi duyệt tích Descartes theo vai.
      // 🔴 US-AI-0439-S04 (@UC9691) — GIỮ vai quản trị, có ĐO: đường TẠO đề xuất
      // (`ai-proposal.service.ts:899-911 resolveAdminFundScope`) trả **403 cho member** ⇒ member không
      // bao giờ có thẻ treo để mà cảnh báo. (Bản nháp của tôi từng mở `["*"]` sau khi chỉ đọc loader
      // — loader lọc `actorUserId` nên trông như "thẻ của chính mình" — rồi phải hoàn nguyên khi đọc
      // tiếp phía TẠO. Tổ hợp member+thẻ-treo nay khai là BẤT KHẢ THI trong lưới PK-1.)
      roles: ["owner", "admin", "treasurer"],
      when: [
        {
          signal: "stalePendingProposals",
          op: "gte",
          value: { threshold: "minStale", fallback: 1 },
        },
        { signal: "stalePendingProposals", op: "gt", value: 0 },
      ],
    },
    vars: { soViec: "stalePendingProposals" },
    steps: [
      { id: "msg", type: "message" },
      {
        id: "cta",
        type: "deep_link",
        deepLinkKey: "FUND_LIST",
        chipPrompt: "Xem các đề xuất đang chờ",
        // Read-tool `pending_items` chưa tồn tại — chủ: `US-AI-0492` (T-10b).
        chipExpect: { type: "answer", query: "pending_items" },
      },
    ],
    configurable: {
      enabled: true,
      band: "IN_FLIGHT",
      itemKind: "task",
      priority: 180,
      templateVi:
        "Có {soViec} đề xuất AI cũ đang treo chờ xác nhận. Rà và huỷ những thẻ không còn đúng — thẻ treo vẫn ghi được sổ nếu người tạo được cấp lại quyền.",
      templateEn:
        "{soViec} stale AI proposal(s) still pending. Review and cancel them — a stale card can still post if its author regains permission.",
      // Ngưỡng: chỉ báo khi số thẻ treo ≥ minStale; `staleAfterMinutes` là hạn "cũ" cho caller đếm.
      thresholds: { minStale: 1, staleAfterMinutes: 60 },
    },
  },

  // ── VỪA XONG VIỆC — lời dẫn đứng trước, không phải một việc mới ────────────────────────────
  //
  // 🔴🔴 US-AI-0443-S03 (@UC9704). Vừa bấm xác nhận **tạo nhóm** xong, mở lại trợ lý mà nghe *"Bạn
  // chưa có nhóm nào — tạo nhóm quỹ đầu tiên nhé?"* thì app **trông như không nhớ gì** — đúng thứ QĐ
  // PO 17/08 gọi là *"app quá ngu"*. Và nó SẼ xảy ra khi ctx chưa kịp nhất quán (replica lag, cache).
  //
  // 🔴 `itemKind: "warning"` là REUSE cơ chế đã có từ 0439 ("luôn hiện, đứng TRÊN CÙNG, không tham
  // gia chọn bậc, không vào badge") — KHÔNG đẻ cơ chế thứ ba, và cũng KHÔNG đặt band riêng: đặt band
  // thì nó sẽ ĐÈ lên việc quan trọng hơn (người vừa tạo nhóm xong vẫn có thể đang nợ tiền).
  {
    id: "SCP-DONE",
    code: "AI-ONB-03",
    kind: "reactive",
    trigger: {
      rule: "RECENTLY_COMPLETED",
      roles: ["*"],
      when: [{ signal: "recentlyCompletedCount", op: "gt", value: 0 }],
    },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      // `warning` không tham gia chọn bậc, nhưng kiểu vẫn đòi khai một bậc hợp lệ.
      band: "IN_FLIGHT",
      itemKind: "warning",
      priority: 199,
      // Không kể TÊN việc vừa xong: tên usecase là mã kỹ thuật (`ONB-CREATE-GROUP`) — đúng thứ PO
      // phàn nàn là vô nghĩa với phụ huynh. Câu chữ nói điều người dùng vừa TRẢI QUA.
      templateVi: "Xong rồi nhé — mình đã ghi lại việc bạn vừa làm.",
      templateEn: "All set — I have recorded what you just did.",
      detailVi: "Nếu danh sách bên dưới chưa kịp cập nhật, mở lại sau một lát là thấy.",
      detailEn: "If the list below has not caught up yet, reopen in a moment and it will be there.",
      ctalessReason: "informational",
    },
  },

  // ── HYGIENE — hồ sơ thiếu thứ sẽ làm hại NGƯỜI DÙNG, không phải thứ làm đẹp hồ sơ ───────────
  //
  // 🔴 US-AI-0442-S02 (@UC9700). Tiêu chí NHẬN một nudge vào đây rất hẹp, và phần bị LOẠI mới là
  // phần quan trọng: avatar (không chặn gì), mô tả nhóm (bỏ trống LÀ một câu trả lời),
  // `transparency_mode` chưa đặt (mặc định fail-closed CÓ CHỦ ĐÍCH — nhắc là đi vận động nới
  // redaction), và **"chưa bật thông báo"** (client CHÍNH không bao giờ gọi `POST /v1/me/device-tokens`
  // ⇒ nudge sẽ bắn cho 100% người dùng về một lỗi CỦA APP).
  //
  // 🔴 `itemKind: "hygiene"` ⇒ tham gia chọn bậc nhưng **KHÔNG vào badge**: người mới toanh thấy badge
  // "3 việc" trong khi không có việc tiền nào là badge nói dối, và badge nói dối là badge người dùng
  // học cách bỏ qua — kể cả lúc nó đếm nợ thật.
  {
    // `HYG-01` — QUAN TRỌNG NHẤT NHÓM: không có khoá tìm được ⇒ **vô hình với mọi lời mời**, vĩnh
    // viễn, và không gì báo. Hậu quả nặng + im lặng ⇒ tạm ẩn 30 ngày, nhãn "Để sau".
    id: "HYG-NO-EMAIL",
    code: "AI-DIS-02",
    kind: "proactive",
    trigger: {
      rule: "NO_EMAIL_IDENTITY",
      roles: ["*"],
      when: [{ signal: "emailIdentityMissing", op: "truthy" }],
    },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "HYGIENE",
      itemKind: "hygiene",
      priority: 55,
      // 🔴 PII: KHÔNG nhắc email/SĐT của ai — chỉ nói "chưa có".
      templateVi: "Tài khoản của bạn chưa có email nên nhóm khác chưa mời bạn vào được.",
      templateEn: "Your account has no email yet, so other groups cannot invite you.",
      detailVi:
        "Thêm email vào hồ sơ để lời mời tìm được bạn. Không có nó, lời mời sẽ lặng lẽ không tới.",
      detailEn: "Add an email so invitations can find you. Without it they silently never arrive.",
      // Chưa có màn "thêm email" nào trợ lý dẫn tới được mà không đẻ một capability GHI mới (cần gate
      // ở CẢ `start()` LẪN `confirm()` — AI-INV-5). Nói thật thay vì dựng nút dẫn vào hư không.
      ctalessReason: "informational",
      snoozeDays: 30,
      dismissLabelVi: "Để sau",
      dismissLabelEn: "Later",
    },
  },
  {
    // `HYG-03` — mất provider = mất tài khoản VÀ toàn bộ lịch sử quỹ. Chỉ nhắc khi đã CÓ GÌ ĐỂ MẤT.
    id: "HYG-SINGLE-PROVIDER",
    code: "AI-DIS-02",
    kind: "proactive",
    trigger: {
      rule: "ACCOUNT_RECOVERY_AT_RISK",
      roles: ["*"],
      when: [
        { signal: "accountRecoveryAtRisk", op: "truthy" },
        // 🔴 "Có gì để mất" = đã có quỹ trong ngữ cảnh. Spec viết `member_dues`, nhưng đo theo QUỸ là
        // đọc đúng ý hơn: chủ nhóm/thủ quỹ giữ vài triệu thường có **0 khoản nợ cá nhân**, mà họ mới
        // là người mất nhiều nhất khi mất tài khoản. Người chưa có quỹ nào thì chọn một provider
        // thường là quyết định CÓ CHỦ Ý — nhắc lúc đó là làm phiền vô ích.
        { signal: "fundCount", op: "gt", value: 0 },
      ],
    },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "HYGIENE",
      itemKind: "hygiene",
      priority: 45,
      templateVi: "Tài khoản của bạn chỉ có một cách đăng nhập — mất nó là mất luôn quyền vào quỹ.",
      templateEn: "Your account has only one way to sign in — losing it locks you out of the fund.",
      detailVi: "Thêm một cách đăng nhập nữa (hoặc đặt mật khẩu) để luôn còn đường vào.",
      detailEn: "Add a second sign-in method (or set a password) so you always have a way back in.",
      ctalessReason: "informational",
      snoozeDays: 30,
      dismissLabelVi: "Để sau",
      dismissLabelEn: "Later",
    },
  },
  {
    // 🔴🔴 `HYG-04` — NUDGE CẤU HÌNH DUY NHẤT ĐƯỢC NHẬN. Bảy nudge cấu hình khác đã bị loại; đọc lý do
    // ở `US-AI-0442/index.md` §2 TRƯỚC KHI thêm cái thứ hai.
    //
    // Vì sao cái này khác: nó KHÔNG nhắc *"bạn chưa cấu hình X"* — mặc định fail-closed `private` là
    // CÓ CHỦ ĐÍCH, và nhắc về nó nghĩa là trợ lý đi **vận động nới redaction**. Nó nhắc một MÂU THUẪN
    // CÓ THẬT giữa hành động của chính chủ nhóm (đã phát link xem công khai) và kết quả thật (người
    // mở link chỉ thấy `'•••'`). Chủ nhóm tưởng mình đã công khai.
    //
    // 🔴 Câu chữ là CÂU HỎI, không phải mệnh lệnh: mở minh bạch là quyết định của nhóm, trợ lý chỉ
    // báo mâu thuẫn. Và KHÔNG nêu số dư — chỉ nêu tên quỹ.
    id: "HYG-PUBLIC-LINK-PRIVATE-FUND",
    code: "AI-DIS-02",
    kind: "proactive",
    trigger: {
      rule: "PUBLIC_LINK_PRIVATE_FUND",
      // Member không sửa được cấu hình quỹ ⇒ nhắc họ là làm phiền (DP-7).
      roles: ["owner", "admin", "treasurer"],
      when: [{ signal: "publicLinkPrivateFund", op: "truthy" }],
    },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "HYGIENE",
      itemKind: "hygiene",
      priority: 35,
      templateVi:
        "Bạn đã phát link xem công khai cho {tenQuy}, nhưng quỹ đang để riêng tư nên người mở link chưa xem được số. Bạn muốn mở cho họ xem không?",
      templateEn:
        "You shared a public view link for {tenQuy}, but the fund is private so visitors cannot see the numbers. Would you like to open it up?",
      detailVi: "Đổi ở phần Cài đặt quỹ. Nếu bạn cố ý để riêng tư thì bỏ qua lời nhắc này.",
      detailEn: "Change it in Fund settings. If you meant to keep it private, just dismiss this.",
      ctalessReason: "informational",
      // Có thể chủ nhóm CỐ Ý (phát link trước, mở số sau) ⇒ bỏ qua VĨNH VIỄN, nhãn "Bỏ qua".
      snoozeDays: 0,
      dismissLabelVi: "Bỏ qua",
      dismissLabelEn: "Dismiss",
    },
  },
  {
    // `HYG-02` — hậu quả NHẸ (bảng thu hiện "Người dùng #1234"): khó chịu, không mất gì ⇒ bỏ qua
    // VĨNH VIỄN, nhãn "Bỏ qua". Tín hiệu đã có sẵn trong ctx ⇒ 0 truy vấn mới.
    id: "HYG-NO-DISPLAY-NAME",
    code: "AI-ONB-09",
    kind: "proactive",
    trigger: {
      rule: "NO_DISPLAY_NAME",
      roles: ["*"],
      when: [{ signal: "displayNameMissing", op: "truthy" }],
    },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "HYGIENE",
      itemKind: "hygiene",
      priority: 30,
      templateVi: "Bạn chưa đặt tên hiển thị nên mọi người khó nhận ra bạn trong bảng thu.",
      templateEn: "You have no display name yet, so others may not recognise you on the board.",
      detailVi: "Đặt tên trong phần Hồ sơ để nhóm biết khoản nào là của bạn.",
      detailEn: "Set your name in Profile so the group knows which entries are yours.",
      ctalessReason: "informational",
      snoozeDays: 0,
      dismissLabelVi: "Bỏ qua",
      dismissLabelEn: "Dismiss",
    },
  },

  // ── IDLE — KHÔNG có việc nào. Hai câu rất khác nhau, đừng gộp ───────────────────────────────
  //
  // 🔴 US-AI-0441-S03 (@UC9698). Hôm nay cả hai kiểu người dùng đều nhận đúng một câu hỏi vu vơ
  // *"Mình giúp gì cho quỹ hôm nay?"*:
  //  · người dùng lâu năm vừa chốt xong đợt thu — thứ họ cần là **xác nhận quỹ đang ổn**, và đó
  //    chính là NIỀM TIN, sản phẩm cốt lõi (`docs/PRD.md:25`);
  //  · người mới chưa có gì — thứ họ cần là **biết trợ lý làm được gì**.
  //
  // 🔴 `itemKind: "idle_prompt"` — câu NỐI VÀO LỜI CHÀO, không phải thẻ việc: nó không vào badge,
  // không tham gia chọn bậc, và **không làm `isEmptyState` hoá false** (nếu không thì chip gợi ý bị
  // tắt đúng lúc cần nhất). Vì sao vẫn là flow: câu chữ phải ở CATALOG để admin sửa được, giống mọi
  // câu khác của lời chào.
  {
    id: "IDL-ALL-CLEAR",
    // `AI-ONB-03` — *"chat không đẩy gì"*. Im lặng khi mọi thứ ổn CŨNG là "không đẩy gì": người dùng
    // không phân biệt được "quỹ đang ổn" với "trợ lý hỏng".
    code: "AI-ONB-03",
    kind: "proactive",
    trigger: {
      rule: "IDLE_ALL_CLEAR",
      roles: ["*"],
      // Nhóm ĐANG VẬN HÀNH = có nhóm **và** có quỹ. Tín hiệu đã có sẵn từ US-AI-0295 — đây là ca duy
      // nhất của cả lane không cần thêm một truy vấn nào.
      when: [
        { signal: "groupCount", op: "gt", value: 0 },
        { signal: "fundCount", op: "gt", value: 0 },
      ],
    },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "IDLE",
      itemKind: "idle_prompt",
      priority: 20,
      templateVi: "Quỹ đang ổn — không có khoản nào chờ bạn.",
      templateEn: "Everything is in order — nothing is waiting on you.",
    },
  },
  {
    id: "IDL-CAPABILITY-HINT",
    // 🔴 `AI-DIS-01` — mã MỒ CÔI đúng chủ đề (*"4 chip tĩnh vĩnh viễn: sau 3 tháng dùng, user vẫn chỉ
    // biết dùng 4 câu"*). Gắn vào mã có sẵn thay vì đẻ mã mới: chip ở đây đến từ `capabilityChips`
    // (theo VAI + config) nên đúng là bản vá cho kịch bản đó.
    code: "AI-DIS-01",
    kind: "proactive",
    trigger: {
      rule: "IDLE_CAPABILITY_HINT",
      roles: ["*"],
      // Loại trừ với `IDL-ALL-CLEAR` bằng CHÍNH một điều kiện, không cần luật triệt tiêu tay: quỹ
      // luôn thuộc một nhóm, nên `fundCount === 0` phủ đúng vế "chưa có nhóm HOẶC chưa có quỹ".
      when: [{ signal: "fundCount", op: "eq", value: 0 }],
    },
    steps: [{ id: "msg", type: "message" }],
    configurable: {
      enabled: true,
      band: "IDLE",
      itemKind: "idle_prompt",
      priority: 15,
      // Câu hỏi mở GIỮ NGUYÊN nguyên văn bản đang chạy (US-AI-0309) — task này đổi *ai nghe câu nào*,
      // không đổi câu. Chip gợi ý đi kèm do caller dựng từ `capabilityChips` (theo vai + config).
      templateVi: "Mình giúp gì cho quỹ hôm nay?",
      templateEn: "How can I help with your fund today?",
    },
  },
];

/** FLOW_CATALOG — immutable, nguồn sự thật của mọi flow AI-first (app thật + preview dùng CHUNG). */
export const FLOW_CATALOG: readonly Flow[] = Object.freeze(FLOWS.map(freezeFlow));

/** Tra flow theo `id` (khoá override admin). */
export function findFlowById(id: string): Flow | undefined {
  return FLOW_CATALOG.find((f) => f.id === id);
}
